// PIREP map icons rendered as colored eyeball glyphs.
//
// Replaces the previous two-circle stack (dark halo + colored fill).
// Boss req 2026-05-13: "For the pirep icon, instead of a colored
// circle, could we make it a colored eyeball?" Reading: PIREP = pilot
// observed it with their own eyes. The eyeball glyph carries that
// semantic at a glance, and the iris color preserves the existing
// hazard-class coding so nothing about scanning the map changes.
//
// Boss req 2026-05-29: "the eyeball is hard to see — make the icon
// bigger and add a colored circle around it that matches the hazard
// class (urgent gets the most prominent color)." Implemented by
// adding a colored outer ring (the "halo" — same hazard-class color
// as the iris, slightly transparent except urgent which is fully
// opaque + thicker stroke), and bumping the on-map icon-size ramp
// in pireps.ts. The eyeball stays at the center of the ring so the
// semantic still reads.
//
// Color mapping mirrors the previous circle fills:
//   urgent → bright red    (#dc2626)  — solid ring, thicker stroke
//   turb   → orange        (#fb923c)  — matches GFA TURB
//   icing  → cyan          (#7dd3fc)  — matches GFA ICE
//   wshear → yellow        (#facc15)  — matches GFA LLWS
//   wx     → purple        (#a855f7)  — wx phenomena
//   cloud  → gray-blue     (#94a3b8)  — routine cloud report
//   other  → neutral gray  (#6b7280)
//
// SVG geometry: 40×40 viewBox (was 32×32). The outer colored ring is
// drawn first; inside it the eye almond-shape is composed of two arcs
// sharing endpoints. The iris is a filled disc carrying the hazard-
// class color; the pupil is a solid black dot. A black halo stroke
// keeps the icon legible over light Sectional or dark satellite
// imagery.

import type { Map as MlMap, ExpressionSpecification } from 'maplibre-gl';
import { ensureSvgImages } from './iconRegistry';

export type PirepHazardClass =
  | 'urgent'
  | 'turb'
  | 'icing'
  | 'wshear'
  | 'wx'
  | 'cloud'
  | 'other';

// Iris colors mirror MapView's previous circle-color match expression
// so the visual language doesn't change for anyone reading a PIREP.
const IRIS: Record<PirepHazardClass, string> = {
  urgent: '#dc2626',
  turb:   '#fb923c',
  icing:  '#7dd3fc',
  wshear: '#facc15',
  wx:     '#a855f7',
  cloud:  '#94a3b8',
  other:  '#6b7280',
};

const SCLERA = '#f8fafc';   // off-white, reads as the white of the eye
const HALO   = '#0b0d0f';   // dark halo for contrast on any basemap
const PUPIL  = '#0b0d0f';

/** Build the SVG markup for one eyeball with the given iris color.
 *  `urgent` gets a thicker, fully-opaque outer ring; everything else
 *  gets a slightly translucent ring so it reads as a halo, not a
 *  competing dot. */
function svgEye(irisColor: string, urgent: boolean): string {
  // 40×40 viewBox (was 32×32). Center (20, 20). The outer hazard ring
  // is drawn first, then a dark halo, then the white sclera + eye
  // anatomy. The eye almond is composed of two arcs:
  //   top arc:    M 8 20 Q 20 9  32 20
  //   bottom arc: Q 20 31 8 20
  // Iris centered (20, 20), r 6.5; pupil r 2.4. Sizes match the
  // previous 32-viewBox version so the eye itself isn't shrunk —
  // it's the *frame around it* that grew, plus an on-screen size bump
  // applied in pireps.ts.
  const ringStroke = urgent ? 3.4 : 2.6;
  const ringOpacity = urgent ? 1.0 : 0.85;
  // Outer ring radius leaves ~1px between ring and the dark halo of
  // the sclera so the colors don't blend.
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
      <!-- Outer hazard-class ring: the new "this is a PIREP" cue. -->
      <circle cx="20" cy="20" r="17.5" fill="none"
              stroke="${irisColor}" stroke-width="${ringStroke}" stroke-opacity="${ringOpacity}" />
      <!-- Sclera + halo: dark halo first, then white sclera fill. -->
      <path d="M8 20 Q 20 9 32 20 Q 20 31 8 20 Z"
            fill="${SCLERA}" stroke="${HALO}" stroke-width="2.0" stroke-linejoin="round" />
      <!-- Iris: filled disc in the hazard-class color. -->
      <circle cx="20" cy="20" r="6.5" fill="${irisColor}" stroke="${HALO}" stroke-width="0.9" />
      <!-- Pupil: solid dot, doubled with a tiny catchlight so the
           eye reads as "alive" instead of dead-pixel. -->
      <circle cx="20" cy="20" r="2.4" fill="${PUPIL}" />
      <circle cx="18.7" cy="18.7" r="0.9" fill="${SCLERA}" opacity="0.85" />
    </svg>
  `.trim();
}

/** Image id used in the MapLibre style (icon-image expression). */
export function pirepIconId(cls: PirepHazardClass): string {
  return `pirep-${cls}`;
}

const SYMBOLS = (Object.keys(IRIS) as PirepHazardClass[]).map((cls) => ({
  id: pirepIconId(cls),
  svg: svgEye(IRIS[cls], cls === 'urgent'),
}));

/**
 * Register every PIREP eyeball icon into the map's image atlas.
 * Idempotent — safe to call from both the map 'load' handler and
 * after a setStyle() rebuild. See airportIcons.ts for the same
 * registration pattern.
 */
export function ensurePirepIcons(map: MlMap): Promise<void> {
  return ensureSvgImages(map, SYMBOLS, {
    defaultSize: 40,
    logTag: '[pirepIcons]',
  });
}

/**
 * MapLibre style expression that picks the right eyeball image based
 * on the feature's hazardClass property. Mirrors the seven-bucket
 * match expression the old circle layer used; falls back to 'other'
 * when hazardClass is missing or unrecognized.
 */
export const PIREP_ICON_EXPRESSION: ExpressionSpecification = [
  'match',
  ['coalesce', ['get', 'hazardClass'], 'other'],
  'urgent', pirepIconId('urgent'),
  'turb',   pirepIconId('turb'),
  'icing',  pirepIconId('icing'),
  'wshear', pirepIconId('wshear'),
  'wx',     pirepIconId('wx'),
  'cloud',  pirepIconId('cloud'),
  /* default */ pirepIconId('other'),
] as unknown as ExpressionSpecification;
